import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  TrendingUp, TrendingDown, Cpu,
  Activity, Radio, Flame, Target, X, ChevronRight,
  Clock, Newspaper, Terminal,
  RefreshCw, Eye, Send, Globe,
} from "lucide-react";
import WorldNewsMap from "./WorldNewsMap";
import BloombergTerminal from "./BloombergTerminal";
import DerivativesStrip from "./DerivativesStrip";
import EconomicCalendar from "./EconomicCalendar";
import OrderbookView from "./OrderbookView";
import { aeonNewsTake } from "./aeonNewsTake";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || "";

const TRACK_SYMS = [
  "BTC","ETH","BNB","SOL","ADA","LINK","DOT","AVAX","POL","XRP",
  "DOGE","UNI","LTC","SUI","NEAR",
];

const COIN_LOGOS = {
  BTC:  "https://assets.coingecko.com/coins/images/1/small/bitcoin.png",
  ETH:  "https://assets.coingecko.com/coins/images/279/small/ethereum.png",
  SOL:  "https://assets.coingecko.com/coins/images/4128/small/solana.png",
  BNB:  "https://assets.coingecko.com/coins/images/825/small/bnb-icon2_2x.png",
  ADA:  "https://assets.coingecko.com/coins/images/975/small/cardano.png",
  LINK: "https://assets.coingecko.com/coins/images/877/small/chainlink-new-logo.png",
  DOT:  "https://assets.coingecko.com/coins/images/12171/small/polkadot.png",
  AVAX: "https://assets.coingecko.com/coins/images/12559/small/Avalanche_Circle_RedWhite_Trans.png",
  POL:  "https://assets.coingecko.com/coins/images/4713/small/matic-token-icon.png",
  XRP:  "https://assets.coingecko.com/coins/images/44/small/xrp-symbol-white-128.png",
  DOGE: "https://assets.coingecko.com/coins/images/5/small/dogecoin.png",
  UNI:  "https://assets.coingecko.com/coins/images/12504/small/uniswap-uni.png",
  LTC:  "https://assets.coingecko.com/coins/images/2/small/litecoin.png",
  SUI:  "https://assets.coingecko.com/coins/images/26375/small/sui_asset.jpeg",
  NEAR: "https://assets.coingecko.com/coins/images/10365/small/near.jpg",
};

const ENGINE_COLORS = {
  FREE_WILL_V2:         { bg:"rgba(168,85,247,0.15)",  border:"rgba(168,85,247,0.4)",  text:"#c084fc" },
  HYPER_ACCURACY:       { bg:"rgba(249,115,22,0.15)",  border:"rgba(249,115,22,0.4)",  text:"#fb923c" },
  AUTONOMOUS_V2:        { bg:"rgba(74,214,255,0.12)",  border:"rgba(74,214,255,0.4)",  text:"#4ad6ff" },
  ELITE_V3:             { bg:"rgba(234,179,8,0.12)",   border:"rgba(234,179,8,0.4)",   text:"#facc15" },
  SCALPER:              { bg:"rgba(59,130,246,0.12)",  border:"rgba(59,130,246,0.4)",  text:"#60a5fa" },
  QUANT_ANALYZER:       { bg:"rgba(99,102,241,0.12)",  border:"rgba(99,102,241,0.4)",  text:"#818cf8" },
  INSTITUTIONAL_SCALPER:{ bg:"rgba(16,185,129,0.12)",  border:"rgba(16,185,129,0.4)",  text:"#34d399" },
  TCN_NEURAL:           { bg:"rgba(239,68,68,0.12)",   border:"rgba(239,68,68,0.4)",   text:"#f87171" },
};

function getGreeting() {
  const h = new Date().getHours();
  if (h >= 5  && h < 12) return { word:"Good morning",   emoji:"☀️",  color:"#fbbf24" };
  if (h >= 12 && h < 17) return { word:"Good afternoon", emoji:"⚡",  color:"#4ad6ff" };
  if (h >= 17 && h < 21) return { word:"Good evening",   emoji:"🌆",  color:"#f97316" };
  return                         { word:"Night watch",    emoji:"🌙",  color:"#a78bfa" };
}

function fmt(p) {
  if (p == null) return "—";
  if (Math.abs(p) >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (Math.abs(p) >= 1)    return p.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  return p.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 });
}

function fmtPrice(price) {
  if (!price && price !== 0) return "—";
  if (price >= 1000) return `$${(price / 1000).toFixed(2)}k`;
  if (price >= 1)    return `$${price.toFixed(2)}`;
  return `$${price.toFixed(4)}`;
}

function ago(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60)    return `${s}s`;
  if (s < 3600)  return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
}

function newsAgo(pubStr) {
  if (!pubStr) return "";
  try {
    const s = Math.floor((Date.now() - new Date(pubStr).getTime()) / 1000);
    if (s < 3600)  return `${Math.floor(s/60)}m ago`;
    if (s < 86400) return `${Math.floor(s/3600)}h ago`;
    return `${Math.floor(s/86400)}d ago`;
  } catch { return ""; }
}

function sentimentFromItem(item) {
  if (!item?.sentiment) return { label: "neutral", color: "var(--text-mute)", emoji: "" };
  const raw = typeof item.sentiment === "object" ? item.sentiment?.label || "" : String(item.sentiment);
  const lbl = raw.toLowerCase();
  const emoji = typeof item.sentiment === "object" ? (item.sentiment?.emoji || "") : "";
  const color = lbl.includes("positive") || lbl.includes("bullish")
    ? "var(--emerald)"
    : lbl.includes("negative") || lbl.includes("bearish")
    ? "var(--rose)"
    : "var(--text-mute)";
  return { label: lbl, color, emoji };
}

/* ══════════════════════════════════════════════════
   LIVE BANNER — auto-rotates news + analysis
══════════════════════════════════════════════════ */
function LiveBanner({ news, hubState, btcPrice, coins, dataAvailability }) {
  const [idx, setIdx]       = useState(0);
  const [visible, setVisible] = useState(true);
  const timerRef = useRef(null);

  /* Build pool: news headlines + generated analysis bullets */
  const pool = useMemo(() => {
    const items = [];

    /* News headlines */
    (news || []).slice(0, 10).forEach((n) => {
      const s = sentimentFromItem(n);
      items.push({
        tag: "NEWS",
        tagColor: "#f5b042",
        text: n.title,
        sub: `${n.source || "crypto"} · ${newsAgo(n.published)}`,
        accent: s.color,
      });
    });

    /* Market analysis bullets from live data */
    const regime = String(hubState?.system?.regime || "RANGING").toUpperCase();
    const health = hubState?.system?.healthScore || 80;
    const heat   = hubState?.exposure?.portfolioHeat || 0;
    const engines = hubState?.system?.activeEngines || 7;

    if (dataAvailability?.system || dataAvailability?.engines) {
      items.push({
        tag: "REGIME",
        tagColor: "#4ad6ff",
        text: `Market regime: ${regime} — AEON tracking momentum across ${engines} active engines`,
        sub: dataAvailability?.system ? `System health ${health}/100` : "Engine telemetry live",
        accent: regime.includes("BULL") ? "var(--emerald)" : regime.includes("BEAR") ? "var(--rose)" : "var(--cyan)",
      });
    }

    if (dataAvailability?.portfolio) {
      items.push({
        tag: "RISK",
        tagColor: heat > 70 ? "#ff5d6c" : heat > 40 ? "#f5b042" : "#4ade80",
        text: `Portfolio heat at ${heat}% — ${heat > 70 ? "elevated risk, gates tightening" : heat > 40 ? "moderate exposure, monitoring" : "low heat, opportunity window open"}`,
        sub: `${engines}/9 engines active`,
        accent: heat > 70 ? "var(--rose)" : heat > 40 ? "var(--amber)" : "var(--emerald)",
      });
    }

    /* BTC price context */
    if (btcPrice) {
      const btc = (coins || []).find(c => c?.symbol === "BTC");
      const chg = typeof btc?.change === "number" ? btc.change : null;
      items.push({
        tag: "BTC",
        tagColor: "#f7931a",
        text: `Bitcoin trading at ${fmtPrice(Number(btcPrice))}${chg != null ? ` — ${chg >= 0 ? "+" : ""}${Number(chg).toFixed(2)}% 24h` : ""}`,
        sub: "OKX live feed",
        accent: chg != null && chg >= 0 ? "var(--emerald)" : "var(--rose)",
      });
    }

    /* SOL, ETH context from coins */
    ["ETH","SOL","BNB"].forEach((sym) => {
      const c = (coins || []).find(x => x?.symbol === sym);
      if (c?.price && typeof c.change === "number") {
        items.push({
          tag: "MARKET",
          tagColor: "#b377ff",
          text: `${sym} at ${fmtPrice(c.price)} — ${c.change >= 0 ? "+" : ""}${Number(c.change).toFixed(2)}% in the last 24 hours`,
          sub: "LiveCoinWatch",
          accent: c.change >= 0 ? "var(--emerald)" : "var(--rose)",
        });
      }
    });

    return items.length > 0 ? items : [{
      tag: "STATUS",
      tagColor: "#4ad6ff",
      text: "Connected and waiting for live market evidence — no directional posture is being inferred yet",
      sub: "Evidence gate active",
      accent: "var(--cyan)",
    }];
  }, [news, hubState, btcPrice, coins, dataAvailability]);

  /* Rotate with fade */
  useEffect(() => {
    if (pool.length < 2) return;
    timerRef.current = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setIdx(i => (i + 1) % pool.length);
        setVisible(true);
      }, 420);
    }, 7000);
    return () => clearInterval(timerRef.current);
  }, [pool]);

  const item = pool[idx] || pool[0];
  if (!item) return null;

  return (
    <div className="lbanner">
      <div className="lbanner-inner" style={{ opacity: visible ? 1 : 0 }}>
        <span className="lbanner-tag" style={{ color: item.tagColor, borderColor: `${item.tagColor}44`, background: `${item.tagColor}18` }}>
          <span className="lbanner-dot" style={{ background: item.tagColor }} />
          {item.tag}
        </span>
        <span className="lbanner-text" style={{ borderLeft: `2px solid ${item.accent}` }}>
          {item.text}
        </span>
        {item.sub && <span className="lbanner-sub">{item.sub}</span>}
        <span className="lbanner-counter">{idx + 1}/{pool.length}</span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════
   WELCOME HERO
══════════════════════════════════════════════════ */
function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function medianOf(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

function signedPct(value, digits = 2) {
  if (!Number.isFinite(value)) return "—";
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}%`;
}

function fmtUsdCompact(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "—";
  const absolute = Math.abs(number);
  if (absolute >= 1e9) return `$${(number / 1e9).toFixed(2)}B`;
  if (absolute >= 1e6) return `$${(number / 1e6).toFixed(2)}M`;
  if (absolute >= 1e3) return `$${(number / 1e3).toFixed(1)}K`;
  return number.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

function freshnessLabel(timestamp) {
  if (!timestamp) return "Awaiting first update";
  const seconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (seconds < 10) return "Updated just now";
  if (seconds < 60) return `Updated ${seconds}s ago`;
  return `Updated ${Math.floor(seconds / 60)}m ago`;
}

function buildSessionIntelligence({
  btcPrice, hubState, wsConnected, coins, news, positions,
  totalPnl, winRate, totalTrades, whaleActivity, dataAvailability,
}) {
  const safeCoins = Array.isArray(coins) ? coins : [];
  const safeNews = Array.isArray(news) ? news : [];
  const safePositions = Array.isArray(positions) ? positions : [];
  const bySymbol = new Map();

  safeCoins.forEach((coin) => {
    const symbol = String(coin?.symbol || "").toUpperCase();
    if (!TRACK_SYMS.includes(symbol)) return;
    const hasPrice = coin?.price !== null && coin?.price !== undefined && coin?.price !== "";
    const hasChange = coin?.change !== null && coin?.change !== undefined && coin?.change !== "";
    const price = hasPrice ? Number(coin.price) : null;
    const change = hasChange ? Number(coin.change) : null;
    bySymbol.set(symbol, {
      symbol,
      price: Number.isFinite(price) && price > 0 ? price : null,
      change: Number.isFinite(change) ? change : null,
    });
  });

  const currentBtc = bySymbol.get("BTC") || { symbol: "BTC", price: null, change: null };
  const numericBtcPrice = Number(btcPrice);
  if (Number.isFinite(numericBtcPrice) && numericBtcPrice > 0) {
    bySymbol.set("BTC", { ...currentBtc, price: numericBtcPrice });
  }

  const tracked = TRACK_SYMS.map((symbol) => bySymbol.get(symbol)).filter(Boolean);
  const priced = tracked.filter((coin) => Number.isFinite(coin.price));
  const changed = tracked.filter((coin) => Number.isFinite(coin.change));
  const changes = changed.map((coin) => coin.change);
  const advancers = changed.filter((coin) => coin.change > 0.05);
  const decliners = changed.filter((coin) => coin.change < -0.05);
  const unchanged = changed.length - advancers.length - decliners.length;
  const medianChange = medianOf(changes);
  const averageChange = changes.length
    ? changes.reduce((sum, value) => sum + value, 0) / changes.length
    : null;
  const ranked = [...changed].sort((a, b) => b.change - a.change);
  const leader = ranked[0] || null;
  const laggard = ranked[ranked.length - 1] || null;
  const dispersion = leader && laggard ? leader.change - laggard.change : null;
  const btc = bySymbol.get("BTC") || currentBtc;
  const breadthBalance = changed.length
    ? (advancers.length - decliners.length) / changed.length
    : 0;

  const hasMarketEvidence = changed.length >= 3 || Number.isFinite(btc.change);
  const btcContribution = Number.isFinite(btc.change)
    ? clampNumber(btc.change * 3.5, -17, 17)
    : 0;
  const medianContribution = Number.isFinite(medianChange)
    ? clampNumber(medianChange * 4.5, -14, 14)
    : 0;
  const breadthContribution = breadthBalance * 19;
  const postureScore = hasMarketEvidence
    ? Math.round(clampNumber(50 + btcContribution + medianContribution + breadthContribution, 0, 100))
    : null;

  let posture = { label: "Awaiting market feed", tone: "pending", short: "Pending" };
  if (postureScore != null && postureScore >= 70) posture = { label: "Risk-on momentum", tone: "positive", short: "Risk-on" };
  else if (postureScore != null && postureScore >= 58) posture = { label: "Constructive", tone: "positive", short: "Constructive" };
  else if (postureScore != null && postureScore >= 43) posture = { label: "Balanced / mixed", tone: "neutral", short: "Balanced" };
  else if (postureScore != null && postureScore >= 31) posture = { label: "Defensive", tone: "negative", short: "Defensive" };
  else if (postureScore != null) posture = { label: "Risk-off pressure", tone: "negative", short: "Risk-off" };

  const positiveNews = safeNews.filter((item) => {
    const label = sentimentFromItem(item).label;
    return label.includes("positive") || label.includes("bullish");
  }).length;
  const negativeNews = safeNews.filter((item) => {
    const label = sentimentFromItem(item).label;
    return label.includes("negative") || label.includes("bearish");
  }).length;
  const neutralNews = Math.max(0, safeNews.length - positiveNews - negativeNews);
  const sourceCount = new Set(safeNews.map((item) => item?.source).filter(Boolean)).size;

  const health = Math.round(Number(hubState?.system?.healthScore || 0));
  const portfolioHeat = Math.round(Number(hubState?.exposure?.portfolioHeat || 0));
  const activeEngines = Number(hubState?.system?.activeEngines || 0);
  const totalEngines = Number(hubState?.system?.totalEngines || 9);
  const regime = String(hubState?.system?.regime || "Ranging").replace(/_/g, " ");
  const riskState = String(hubState?.system?.riskState || "stable").replace(/_/g, " ");
  const topEngine = [...(hubState?.engines || [])].sort((a, b) => Number(b.edge || 0) - Number(a.edge || 0))[0] || null;
  const performanceAvailable = Boolean(dataAvailability?.performance);
  const portfolioAvailable = Boolean(dataAvailability?.portfolio);
  const systemAvailable = Boolean(dataAvailability?.system);
  const enginesAvailable = Boolean(dataAvailability?.engines);

  const longPositions = safePositions.filter((position) => String(position?.direction || "").toUpperCase() === "LONG").length;
  const shortPositions = safePositions.filter((position) => String(position?.direction || "").toUpperCase() === "SHORT").length;
  const openNotional = safePositions.reduce((sum, position) => sum + Number(position?.position_size_usd || 0), 0);
  const unrealizedPnl = safePositions.reduce((sum, position) => sum + Number(position?.unrealized_pnl || 0), 0);
  const positionBias = safePositions.length === 0
    ? "No exposure"
    : longPositions === shortPositions
      ? "Balanced book"
      : longPositions > shortPositions
        ? `${longPositions}L / ${shortPositions}S`
        : `${shortPositions}S / ${longPositions}L`;

  const priceCoverage = changed.length / TRACK_SYMS.length;
  const evidenceScore = Math.round(clampNumber(
    priceCoverage * 55
      + (Number.isFinite(btc.price) ? 10 : 0)
      + (safeNews.length ? 10 : 0)
      + (systemAvailable ? 10 : 0)
      + (portfolioAvailable ? 5 : 0)
      + (wsConnected ? 10 : 3),
    0,
    100,
  ));

  const breadthText = changed.length
    ? `${advancers.length} advancing, ${decliners.length} declining and ${unchanged} flat across ${changed.length} tracked markets`
    : "tracked-market breadth is waiting for the live price feed";
  const anchorText = Number.isFinite(btc.change)
    ? `BTC is ${signedPct(btc.change)} over 24h`
    : "BTC 24h direction is not available yet";
  const leadershipText = leader && laggard
    ? `${leader.symbol} leads at ${signedPct(leader.change)}, while ${laggard.symbol} trails at ${signedPct(laggard.change)}`
    : "cross-asset leadership will appear when coverage improves";
  const riskEvidence = [
    portfolioAvailable ? `${portfolioHeat}% portfolio heat` : null,
    systemAvailable ? `${health}/100 system health` : null,
    enginesAvailable ? `${activeEngines}/${totalEngines} engines online` : null,
  ].filter(Boolean);
  const riskText = riskEvidence.length
    ? `AEON telemetry reports ${riskEvidence.join(", ")}`
    : "AEON risk and system telemetry are awaiting verified source data";
  const executionText = safePositions.length
    ? `${safePositions.length} open positions carry ${positionBias.toLowerCase()}, ${fmtUsdCompact(openNotional)} notional and ${unrealizedPnl >= 0 ? "+" : "-"}$${Math.abs(unrealizedPnl).toFixed(2)} unrealized PnL`
    : "the execution book is currently flat with no active positions";

  return {
    posture,
    postureScore,
    evidenceScore,
    regime,
    riskState,
    health,
    portfolioHeat,
    activeEngines,
    totalEngines,
    performanceAvailable,
    portfolioAvailable,
    systemAvailable,
    enginesAvailable,
    btc,
    pricedCount: priced.length,
    changedCount: changed.length,
    advancers: advancers.length,
    decliners: decliners.length,
    unchanged,
    breadthBalance,
    medianChange,
    averageChange,
    leader,
    laggard,
    dispersion,
    positiveNews,
    negativeNews,
    neutralNews,
    sourceCount,
    newsCount: safeNews.length,
    topHeadline: safeNews[0]?.title || null,
    positionCount: safePositions.length,
    positionBias,
    openNotional,
    unrealizedPnl,
    topEngine,
    whaleActivity,
    todayPnl: performanceAvailable ? Number(hubState?.system?.todayPnl ?? totalPnl ?? 0) : null,
    totalPnl: performanceAvailable ? Number(totalPnl || 0) : null,
    winRate: performanceAvailable ? Number(winRate || 0) : null,
    totalTrades: performanceAvailable ? Number(totalTrades || 0) : null,
    summary: `${breadthText}. ${anchorText}; ${leadershipText}. ${riskText}, and ${executionText}.`,
    drivers: [
      {
        label: "Market breadth",
        value: changed.length ? `${advancers.length}/${changed.length} advancing` : "Awaiting coverage",
        detail: changed.length
          ? `Median ${signedPct(medianChange)} · average ${signedPct(averageChange)}`
          : "No market conclusion is being inferred from missing prices.",
        tone: breadthBalance > 0.15 ? "positive" : breadthBalance < -0.15 ? "negative" : "neutral",
      },
      {
        label: "BTC anchor",
        value: Number.isFinite(btc.price) ? fmtPrice(btc.price) : "Price unavailable",
        detail: Number.isFinite(btc.change) ? `${signedPct(btc.change)} over 24 hours` : "24h change awaiting source data.",
        tone: Number.isFinite(btc.change) ? (btc.change > 0.25 ? "positive" : btc.change < -0.25 ? "negative" : "neutral") : "pending",
      },
      {
        label: "Risk & execution",
        value: portfolioAvailable ? `${portfolioHeat}% heat · ${safePositions.length} open` : `${safePositions.length} open · risk feed pending`,
        detail: portfolioAvailable ? `${riskState} risk state · ${positionBias.toLowerCase()}` : `${positionBias.toLowerCase()} · no risk estimate inferred`,
        tone: portfolioAvailable
          ? (portfolioHeat >= 70 ? "negative" : portfolioHeat >= 45 ? "neutral" : "positive")
          : "pending",
      },
      {
        label: "Intelligence flow",
        value: safeNews.length ? `${safeNews.length} headlines · ${sourceCount} sources` : "Awaiting headlines",
        detail: safeNews.length
          ? `${positiveNews} positive · ${negativeNews} negative · ${neutralNews} neutral`
          : "News sentiment is excluded until sources respond.",
        tone: positiveNews > negativeNews ? "positive" : negativeNews > positiveNews ? "negative" : "neutral",
      },
    ],
  };
}

function WelcomeHero({
  btcPrice, hubState, wsConnected, coins, news, positions,
  totalPnl, winRate, totalTrades, whaleActivity, feedTimes, dataAvailability,
}) {
  const [timeStr, setTimeStr] = useState(() =>
    new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })
  );
  useEffect(() => {
    const t = setInterval(() =>
      setTimeStr(new Date().toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" }))
    , 30000);
    return () => clearInterval(t);
  }, []);

  const g = getGreeting();
  const dateStr = new Date().toLocaleDateString([], { weekday:"short", month:"short", day:"numeric" });
  const intel = useMemo(() => buildSessionIntelligence({
    btcPrice, hubState, wsConnected, coins, news, positions,
    totalPnl, winRate, totalTrades, whaleActivity, dataAvailability,
  }), [
    btcPrice, hubState, wsConnected, coins, news, positions,
    totalPnl, winRate, totalTrades, whaleActivity, dataAvailability,
  ]);

  return (
    <section className="hub-hero" aria-labelledby="hub-overview-title">
      <div className="hub-hero-glow" />
      <div className="hub-hero-top">
        <div className="hub-hero-greeting-row">
          <div className="hub-session-line">
            <span className="hub-session-kicker">
              <span>01</span>
              Session overview
            </span>
            <span className={`hub-ws-pill ${wsConnected ? "is-live" : "is-offline"}`}>
              <span className="hub-ws-dot" />
              {wsConnected ? "Live data" : "Reconnecting"}
            </span>
          </div>

          <div className="hub-hero-text">
            <h1 id="hub-overview-title" className="hub-hero-title">
              {g.word}, <span>Tovar.</span>
            </h1>
            <p className="hub-hero-briefing">A live, evidence-backed read of market direction, risk and system readiness.</p>
          </div>

          <div className="hub-session-read">
            <span>What is happening now</span>
            <p>{intel.summary}</p>
          </div>

          <div className="hub-record-grid">
            <div>
              <span>Live PnL</span>
              <strong className={intel.unrealizedPnl > 0 ? "is-positive" : intel.unrealizedPnl < 0 ? "is-negative" : ""}>
                {intel.positionCount ? `${intel.unrealizedPnl >= 0 ? "+" : "-"}$${Math.abs(intel.unrealizedPnl).toFixed(2)}` : "—"}
              </strong>
            </div>
            <div>
              <span>Win rate</span>
              <strong>{intel.performanceAvailable ? `${intel.winRate.toFixed(1)}%` : "—"}</strong>
            </div>
            <div>
              <span>Trades recorded</span>
              <strong>{intel.performanceAvailable ? intel.totalTrades.toLocaleString() : "—"}</strong>
            </div>
          </div>

          <div className="hub-hero-meta">
            <span>{dateStr}</span>
            <i />
            <span>{timeStr}</span>
            <i />
            <span>Local command desk</span>
          </div>
        </div>

        <div className="hub-market-brief">
          <div className="hmb-header">
            <div>
              <span className="hmb-label">Live market posture</span>
              <strong className={`hmb-posture-title is-${intel.posture.tone}`}>
                <span className="hmb-mood-dot" />
                {intel.posture.label}
              </strong>
            </div>
            <div className="hmb-header-badges">
              <span className="hmb-evidence">{intel.evidenceScore}% evidence</span>
              <span className="hmb-regime">{intel.regime}</span>
            </div>
          </div>

          <div className="hmb-posture-explainer">
            <div className="hmb-posture-score">
              <span>Posture score</span>
              <strong>{intel.postureScore == null ? "—" : intel.postureScore}</strong>
              <small>/100</small>
            </div>
            <div className="hmb-posture-copy">
              <p>
                {intel.postureScore == null
                  ? "A directional posture is intentionally withheld until enough live prices arrive."
                  : `The ${intel.posture.short.toLowerCase()} reading is weighted from BTC 24h direction, tracked-market breadth and the median market move.`}
              </p>
              <div className={`hmb-score-track is-${intel.posture.tone}`}>
                <span style={{ width: `${intel.postureScore ?? 0}%` }} />
                {intel.postureScore != null ? <i style={{ left: `${intel.postureScore}%` }} /> : null}
              </div>
              <div className="hmb-score-axis">
                <span>Risk-off</span>
                <span>Balanced</span>
                <span>Risk-on</span>
              </div>
            </div>
          </div>

          <div className="hmb-metrics">
            <div className="hmb-metric">
              <span>BTC · 24h</span>
              <strong>{signedPct(intel.btc.change)}</strong>
              <small>{Number.isFinite(intel.btc.price) ? fmtPrice(intel.btc.price) : "Awaiting price"}</small>
            </div>
            <div className="hmb-metric">
              <span>Market breadth</span>
              <strong>{intel.changedCount ? `${intel.advancers} / ${intel.decliners}` : "—"}</strong>
              <small>Advancing / declining</small>
            </div>
            <div className="hmb-metric">
              <span>Median move</span>
              <strong>{signedPct(intel.medianChange)}</strong>
              <small>{intel.changedCount}/{TRACK_SYMS.length} markets covered</small>
            </div>
            <div className="hmb-metric">
              <span>Dispersion</span>
              <strong>{Number.isFinite(intel.dispersion) ? `${intel.dispersion.toFixed(2)}%` : "—"}</strong>
              <small>{intel.leader && intel.laggard ? `${intel.leader.symbol} → ${intel.laggard.symbol}` : "Awaiting cross-section"}</small>
            </div>
            <div className="hmb-metric">
              <span>Portfolio heat</span>
              <strong>{intel.portfolioAvailable ? <>{intel.portfolioHeat}<small>%</small></> : "—"}</strong>
              <small>{intel.portfolioAvailable ? `${intel.riskState} risk state` : "Awaiting risk snapshot"}</small>
            </div>
            <div className="hmb-metric">
              <span>Execution book</span>
              <strong>{intel.positionCount}</strong>
              <small>{intel.positionBias}</small>
            </div>
          </div>

          <div className="hmb-driver-grid">
            {intel.drivers.map((driver) => (
              <div key={driver.label} className={`hmb-driver is-${driver.tone}`}>
                <span>{driver.label}</span>
                <strong>{driver.value}</strong>
                <small>{driver.detail}</small>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="hub-evidence-strip">
        <div className="hub-evidence-intro">
          <span>Evidence & freshness</span>
          <strong>No unsupported assumptions</strong>
        </div>
        <div className="hub-evidence-source">
          <span>Market data</span>
          <strong>{intel.changedCount}/{TRACK_SYMS.length} tracked changes</strong>
          <small>{freshnessLabel(feedTimes?.market)}</small>
        </div>
        <div className="hub-evidence-source">
          <span>System telemetry</span>
          <strong>
            {intel.systemAvailable || intel.enginesAvailable
              ? `${intel.enginesAvailable ? `${intel.activeEngines}/${intel.totalEngines} engines` : "Engine status pending"} · ${intel.systemAvailable ? `${intel.health}/100 health` : "health pending"}`
              : "Awaiting verified telemetry"}
          </strong>
          <small>{wsConnected ? "Transport connected" : "Connection recovering"}</small>
        </div>
        <div className="hub-evidence-source">
          <span>Execution</span>
          <strong>{intel.positionCount} open · {fmtUsdCompact(intel.openNotional)} notional</strong>
          <small>{freshnessLabel(feedTimes?.positions)}</small>
        </div>
        <div className="hub-evidence-source">
          <span>Intelligence</span>
          <strong>{intel.newsCount} headlines · {intel.sourceCount} sources</strong>
          <small>{freshnessLabel(feedTimes?.news)}</small>
        </div>
        <div className="hub-evidence-source">
          <span>Whale monitor</span>
          <strong>{intel.whaleActivity?.activity || "—"}</strong>
          <small>{intel.whaleActivity?.message || "Awaiting activity signal"}</small>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════
   CALLING CARD MODAL — exchange style
══════════════════════════════════════════════════ */
function CallingCardModal({ pos, onClose, onClosePosition }) {
  if (!pos) return null;
  const sym    = pos.symbol?.replace("/USDT","") || "";
  const isLong = pos.direction === "LONG";
  const pnl    = pos.unrealized_pnl ?? 0;
  const pnlPct = pos.unrealized_pnl_pct ?? 0;
  const profit = pnl >= 0;
  const logo   = COIN_LOGOS[sym];
  const range  = Math.abs((pos.take_profit||0) - (pos.stop_loss||0));
  const progress = range > 0 ? Math.max(0, Math.min(100, ((pos.current_price - pos.stop_loss) / range) * 100)) : 50;
  const ec     = ENGINE_COLORS[pos.strategy] || { bg:"rgba(255,255,255,0.06)", border:"rgba(255,255,255,0.15)", text:"var(--text-dim)" };
  const sc     = isLong ? "#4ade80" : "#ff5d6c";
  const sbg    = isLong ? "rgba(74,222,128,0.1)" : "rgba(255,93,108,0.1)";

  return (
    <div className="xcard-overlay" onClick={onClose}>
      <div className="xcard-shell" onClick={e => e.stopPropagation()}>
        {/* header */}
        <div className="xcard-hbar" style={{ background:`linear-gradient(135deg,${sbg},transparent)` }}>
          <div className="xcard-hbar-left">
            <div className="xcard-coin-ring" style={{ borderColor:sc }}>
              {logo ? <img src={logo} alt={sym} width={28} height={28} style={{ borderRadius:"50%" }} />
                    : <span style={{ fontSize:12,fontWeight:800,color:"#fff" }}>{sym.slice(0,2)}</span>}
            </div>
            <div>
              <div className="xcard-pair">{sym}<span style={{ color:"var(--text-mute)" }}>/USDT</span></div>
              <div className="xcard-meta-row">
                <span className="xcard-side-badge" style={{ background:sbg, color:sc, borderColor:sc }}>{pos.direction}</span>
                <span className="xcard-lev-badge">{pos.leverage}x</span>
                <span className="xcard-perp-badge">PERP</span>
              </div>
            </div>
          </div>
          <div className="xcard-hbar-right">
            <div className="xcard-live-row">
              <span className="xcard-live-dot" style={{ background:sc }} />
              <span className="xcard-live-txt">LIVE</span>
            </div>
            <button type="button" className="xcard-close" onClick={onClose}><X size={16} /></button>
          </div>
        </div>

        {/* PnL */}
        <div className="xcard-pnl-block" style={{ borderColor: profit?"rgba(74,222,128,0.2)":"rgba(255,93,108,0.2)" }}>
          <div className="xcard-pnl-left">
            <p className="xcard-pnl-label">Unrealized PnL</p>
            <p className="xcard-pnl-big" style={{ color:profit?"#4ade80":"#ff5d6c" }}>
              {profit?"+":""}{pnlPct.toFixed(2)}%
            </p>
            <p className="xcard-pnl-usd" style={{ color:profit?"#4ade80":"#ff5d6c" }}>
              {profit?"+":""}${fmt(pnl)}
            </p>
          </div>
          <div className="xcard-rr-block">
            {Math.abs(pnlPct) > 30 && <Flame size={18} style={{ color:profit?"#4ade80":"#ff5d6c", marginBottom:4 }} />}
            <p className="xcard-rr-label">R:R Ratio</p>
            <p className="xcard-rr-val">{(pos.rr_ratio||0).toFixed(2)}x</p>
            <p className="xcard-rr-acct">{pos.account_emoji} {pos.account_id}</p>
          </div>
        </div>

        {/* SL/TP bar */}
        <div className="xcard-sltp">
          <div className="xcard-sltp-labels">
            <span style={{ color:"#ff5d6c" }}>SL ${fmt(pos.stop_loss)}</span>
            <span style={{ color:"var(--text-mute)",fontSize:10 }}>Progress</span>
            <span style={{ color:"#4ade80" }}>TP ${fmt(pos.take_profit)}</span>
          </div>
          <div className="xcard-sltp-track">
            <div className="xcard-sltp-fill" style={{
              width:`${progress}%`,
              background: profit ? "linear-gradient(90deg,rgba(74,222,128,0.4),#4ade80)" : "linear-gradient(90deg,rgba(255,93,108,0.4),#ff5d6c)",
            }} />
            <div className="xcard-sltp-thumb" style={{ left:`${progress}%`, background:profit?"#4ade80":"#ff5d6c" }} />
          </div>
        </div>

        {/* Data grid */}
        <div className="xcard-data-grid">
          {[
            { label:"Entry",    val:fmt(pos.entry_price),         color:"var(--text)" },
            { label:"Mark",     val:fmt(pos.current_price),        color:profit?"#4ade80":"#ff5d6c" },
            { label:"Liq.",     val:fmt(pos.liquidation_price),    color:"#f5b042" },
            { label:"Size",     val:`$${fmt(pos.position_size_usd)}`, color:"var(--text)" },
            { label:"Opened",   val:ago(pos.opened_at),           color:"var(--text-dim)" },
            { label:"Conf.",    val:`${pos.signal_data?.confidence||"?"}%`, color:"#4ad6ff" },
          ].map(({ label, val, color }) => (
            <div key={label} className="xcard-data-cell">
              <p className="xcard-data-label">{label}</p>
              <p className="xcard-data-val" style={{ color }}>{val}</p>
            </div>
          ))}
        </div>

        {/* Engine + close */}
        <div className="xcard-footer">
          <span className="xcard-engine-chip" style={{ background:ec.bg, border:`1px solid ${ec.border}`, color:ec.text }}>
            <Cpu size={11} /> {pos.strategy}
          </span>
          {onClosePosition && (
            <button type="button" className="xcard-close-pos" onClick={() => onClosePosition(pos.account_id, pos.symbol)}>
              <X size={11} /> Close Position
            </button>
          )}
        </div>

        {/* Signal confirmations */}
        {Array.isArray(pos.signal_data?.confirmations) && pos.signal_data.confirmations.length > 0 && (
          <div className="xcard-sigs">
            {pos.signal_data.confirmations.slice(0,6).map((c,i) => (
              <span key={i} className="xcard-sig-pill">
                <span className="xcard-sig-dot" style={{ background:"#4ade80" }} />
                {typeof c === "string" ? c.slice(0,40) : (c?.name || "")}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Compact position card ── */
function PositionCard({ pos, onClick }) {
  const sym    = pos.symbol?.replace("/USDT","") || "";
  const isLong = pos.direction === "LONG";
  const profit = (pos.unrealized_pnl ?? 0) >= 0;
  const logo   = COIN_LOGOS[sym];
  const pnlPct = pos.unrealized_pnl_pct ?? 0;
  const sc     = isLong ? "#4ade80" : "#ff5d6c";

  return (
    <div className="pos-card" onClick={onClick}>
      <div className="pos-card-side" style={{ background:sc }} />
      <div className="pos-card-body">
        <div className="pos-card-top">
          <div className="pos-card-coin">
            {logo ? <img src={logo} alt={sym} width={16} height={16} style={{ borderRadius:"50%", flexShrink:0 }} />
                  : <span className="pos-card-coin-abbr">{sym.slice(0,2)}</span>}
            <span className="pos-card-sym">{sym}</span>
            <span className="pos-card-dir" style={{ color:sc }}>{pos.direction}</span>
            <span className="pos-card-lev">{pos.leverage}x</span>
          </div>
          <div className="pos-card-pnl" style={{ color:profit?sc:"#ff5d6c" }}>
            {profit?"+":""}{pnlPct.toFixed(2)}%
          </div>
        </div>
        <div className="pos-card-bot">
          <span className="pos-card-acct">{pos.account_emoji} {pos.account_id}</span>
          <span className="pos-card-usd" style={{ color:profit?"#4ade80":"#ff5d6c" }}>
            {profit?"+":""}${fmt(pos.unrealized_pnl)}
          </span>
        </div>
      </div>
      <ChevronRight size={13} style={{ color:"rgba(255,255,255,0.2)", flexShrink:0 }} />
    </div>
  );
}

/* ── Ticker item ── */
function TickerItem({ symbol, price, change }) {
  const up = change == null || change >= 0;
  return (
    <span className="hc-ticker-item">
      <span className="hc-ticker-sym">{symbol}</span>
      {price ? <span className="hc-ticker-price">{fmtPrice(price)}</span>
             : <span className="hc-ticker-price" style={{ color:"var(--text-mute)" }}>—</span>}
      {change != null && (
        <span className={`hc-ticker-chg ${up?"is-up":"is-dn"}`}>
          {up?"+":""}{change.toFixed(2)}%
        </span>
      )}
    </span>
  );
}

/* ── Watchlist row — full width, clear price ── */
function WatchRow({ symbol, price, change }) {
  const up   = change == null || change >= 0;
  const logo = COIN_LOGOS[symbol];
  return (
    <div className="wl-row">
      <div className="wl-row-left">
        <div className="wl-icon">
          {logo ? <img src={logo} alt={symbol} width={22} height={22} style={{ borderRadius:"50%" }} />
                : <span className="wl-icon-abbr">{symbol.slice(0,2)}</span>}
        </div>
        <span className="wl-sym">{symbol}</span>
        <span className="wl-pair">/USDT</span>
      </div>
      <div className="wl-row-right">
        <span className="wl-price">{price ? fmtPrice(price) : <span style={{ color:"var(--text-mute)" }}>—</span>}</span>
        {change != null ? (
          <span className={`wl-chg ${up?"is-up":"is-dn"}`}>
            {up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
            {Math.abs(change).toFixed(2)}%
          </span>
        ) : <span className="wl-chg" style={{ color:"var(--text-mute)" }}>—</span>}
      </div>
    </div>
  );
}

/* ── News item ── */
function NewsItem({ item }) {
  const s = sentimentFromItem(item);
  const take = aeonNewsTake(item);
  const hasUrl = !!item.url;
  const Tag = hasUrl ? "a" : "div";
  const linkProps = hasUrl ? {
    href: item.url,
    target: "_blank",
    rel: "noopener noreferrer",
  } : {};
  return (
    <Tag className={`news-item${hasUrl ? " news-item--link" : ""}`} {...linkProps}>
      <div className="news-item-dot" style={{ background:s.color }} />
      <div className="news-item-body">
        <p className="news-item-title">{item.title}</p>
        <div className="news-item-meta">
          <span className="news-item-src">{item.source||"crypto"}</span>
          {item.published && <span className="news-item-ts">{newsAgo(item.published)}</span>}
          {s.label && (
            <span className="news-item-sent" style={{ color:s.color }}>
              {s.emoji} {s.label}
            </span>
          )}
          {hasUrl && <span className="news-item-read">↗ read</span>}
        </div>
        {take && (
          <div className="news-take">
            <span className="news-take-action" style={{ color:take.color, borderColor:`${take.color}55`, background:`${take.color}14` }}>
              {take.action}
            </span>
            <span className="news-take-line">{take.line}</span>
          </div>
        )}
      </div>
    </Tag>
  );
}

const CAT_CFG = {
  CRYPTO:      { color:"#4ad6ff", label:"CRYPTO"    },
  MACRO:       { color:"#f5b042", label:"MACRO"     },
  FED:         { color:"#ff8a4d", label:"FED"       },
  EQUITIES:    { color:"#4ade80", label:"EQUITIES"  },
  COMMODITIES: { color:"#d4a857", label:"COMMODITY" },
  GEO:         { color:"#ff5d6c", label:"GEO"       },
  MARKETS:     { color:"#b377ff", label:"MARKETS"   },
};

/* ── World News Panel — live macro + crypto feed with category filter ── */
function WorldNewsPanel({ news = [], loading = false, onRefresh }) {
  const [activeFilter, setActiveFilter] = useState(null);

  const catCounts = useMemo(() => {
    const c = {};
    news.forEach(n => { const cat = n.category || "CRYPTO"; c[cat] = (c[cat]||0)+1; });
    return c;
  }, [news]);

  const filtered = useMemo(() =>
    activeFilter ? news.filter(n => (n.category||"CRYPTO") === activeFilter) : news
  , [news, activeFilter]);

  return (
    <section className="hc-panel hub-section-worldnews">
      <header className="hc-panel-hdr">
        <Globe size={14} style={{ color:"#f5b042" }} />
        <span>World Intelligence</span>
        <span className="hc-badge" style={{ color:"#f5b042", borderColor:"rgba(245,176,66,0.35)" }}>
          {news.length} headlines
        </span>
        <button type="button" className="hc-news-refresh" onClick={onRefresh} style={{ marginLeft:"auto" }}>
          <RefreshCw size={11} className={loading?"hc-spin":""} />
        </button>
      </header>

      {/* Category filter bar */}
      {Object.keys(catCounts).length > 0 && (
        <div className="world-news-filter-bar">
          <button
            className="world-news-filter-btn"
            onClick={() => setActiveFilter(null)}
            style={{
              border: `1px solid ${!activeFilter ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.12)"}`,
              color: !activeFilter ? "#fff" : "rgba(255,255,255,0.35)",
              background: !activeFilter ? "rgba(255,255,255,0.08)" : "transparent",
            }}>
            ALL {news.length}
          </button>
          {Object.entries(catCounts).sort((a,b) => b[1]-a[1]).map(([cat, cnt]) => {
            const cfg = CAT_CFG[cat] || { color:"#8a93a3", label:cat };
            const isAct = activeFilter === cat;
            return (
              <button key={cat}
                className="world-news-filter-btn"
                onClick={() => setActiveFilter(f => f===cat ? null : cat)}
                style={{
                  border: `1px solid ${isAct ? cfg.color : `${cfg.color}44`}`,
                  color: cfg.color,
                  background: isAct ? `${cfg.color}18` : "transparent",
                }}>
                {cfg.label} {cnt}
              </button>
            );
          })}
        </div>
      )}

      <div style={{ maxHeight: 320, overflowY:"auto", scrollbarWidth:"thin", scrollbarColor:"rgba(255,255,255,0.1) transparent" }}>
        {filtered.length === 0 ? (
          <div className="hc-trade-empty">
            <Globe size={15} style={{ opacity:0.3 }} />
            <span>{loading ? "Loading…" : activeFilter ? `No ${activeFilter} news` : "No world news"}</span>
          </div>
        ) : filtered.map((item, i) => {
          const cfg    = CAT_CFG[item.category||"CRYPTO"] || { color:"#8a93a3", label:item.category||"NEWS" };
          const s      = sentimentFromItem(item);
          const take   = aeonNewsTake(item);
          const hasUrl = !!item.url;
          const Tag    = hasUrl ? "a" : "div";
          const lp     = hasUrl ? { href:item.url, target:"_blank", rel:"noopener noreferrer" } : {};
          return (
            <Tag key={i} className={`world-news-item${hasUrl ? " world-news-item--link" : ""}`} {...lp}>
              <div className="world-news-cat-dot" style={{ background:cfg.color }} />
              <div className="world-news-body">
                <p className="world-news-title">{item.title}</p>
                <div className="world-news-meta">
                  <span className="world-news-cat-pill"
                    style={{ background:`${cfg.color}18`, border:`1px solid ${cfg.color}44`, color:cfg.color }}>
                    {cfg.label}
                  </span>
                  {item.source && <span className="world-news-src">{item.source}</span>}
                  {item.published && <span className="world-news-ts">{newsAgo(item.published)}</span>}
                  {(item.impact||0) >= 70 && (
                    <span className="world-news-impact" style={{ color:"#ff5d6c" }}>▲ HIGH</span>
                  )}
                  {s.label && s.label !== "neutral" && (
                    <span style={{ fontSize:9, color:s.color, fontFamily:"ui-monospace,monospace" }}>
                      {s.emoji} {s.label.toUpperCase()}
                    </span>
                  )}
                  {hasUrl && <span className="world-news-read">↗ read</span>}
                </div>
                {take && (
                  <div className="news-take">
                    <span className="news-take-action" style={{ color:take.color, borderColor:`${take.color}55`, background:`${take.color}14` }}>
                      {take.action}
                    </span>
                    <span className="news-take-line">{take.line}</span>
                  </div>
                )}
              </div>
            </Tag>
          );
        })}
      </div>
    </section>
  );
}

/* ── Engine signal row ── */
const STATUS_COLOR = {
  healthy:"#4ade80", restricted:"#ff5d6c", cooling:"#f5b042",
  "low-data":"var(--text-mute)", stale:"var(--text-mute)",
};

function SignalRow({ engine, perf }) {
  if (!engine || typeof engine !== "object") return null;
  const status = String(engine.status || "unknown");
  const name   = String(engine.name || "—");
  const tier   = String(engine.tier || "?");
  const edge   = Number(engine.edge) || 0;
  const heat   = Number(engine.heat) || 0;
  const color  = STATUS_COLOR[status] || "var(--text-mute)";
  const active = status === "healthy";

  // Pull real performance from /analytics/win-rates payload
  const wins  = Number(perf?.wins || 0);
  const losses = Math.max(0, Number(perf?.trades || 0) - wins);
  const winPct = Number(perf?.win_rate || engine.winRate || 0);
  const pnl    = Number(perf?.total_pnl || 0);
  const trades = Number(perf?.trades || 0);
  const pnlColor = pnl > 0 ? "#4ade80" : pnl < 0 ? "#ff5d6c" : "rgba(255,255,255,0.4)";
  const wrColor  = winPct > 50 ? "#4ade80" : winPct > 35 ? "#f5b042" : "#ff5d6c";

  return (
    <div className="hc-sig-row-v2" data-active={active ? "true" : "false"}>
      <div className="hc-sig-top">
        <span className="hc-sig-dot" style={{ background: color, boxShadow: active ? `0 0 6px ${color}` : "none" }} />
        <span className="hc-sig-name">{name}</span>
        <span className="hc-sig-tier" style={{ borderColor: `${color}55`, color }}>T{tier}</span>
        <span className="hc-sig-status" style={{ color }}>{status.toUpperCase().slice(0, 7)}</span>
      </div>
      {/* Dual-bar: win-rate (top) over edge (bottom). Heat shown as accent. */}
      <div className="hc-sig-bars">
        <div className="hc-sig-bar-row">
          <span className="hc-sig-bar-lbl">WR</span>
          <div className="hc-sig-bar-track">
            <div className="hc-sig-bar-fill" style={{ width: `${winPct}%`, background: wrColor }} />
          </div>
          <span className="hc-sig-bar-val" style={{ color: wrColor }}>{winPct.toFixed(0)}%</span>
        </div>
        <div className="hc-sig-bar-row">
          <span className="hc-sig-bar-lbl">EDGE</span>
          <div className="hc-sig-bar-track">
            <div className="hc-sig-bar-fill" style={{ width: `${edge}%`, background: color, opacity: active ? 1 : 0.4 }} />
          </div>
          <span className="hc-sig-bar-val" style={{ color }}>{edge}%</span>
        </div>
      </div>
      {/* Footer: trades + pnl + heat */}
      <div className="hc-sig-foot">
        <span className="hc-sig-foot-item">
          <span className="hc-sig-foot-lbl">Trades</span>
          <span className="hc-sig-foot-val">{trades}</span>
        </span>
        <span className="hc-sig-foot-item">
          <span className="hc-sig-foot-lbl">PnL</span>
          <span className="hc-sig-foot-val" style={{ color: pnlColor }}>
            {pnl >= 0 ? "+" : "−"}${Math.abs(pnl) >= 1000 ? `${(Math.abs(pnl) / 1000).toFixed(1)}k` : Math.abs(pnl).toFixed(0)}
          </span>
        </span>
        <span className="hc-sig-foot-item">
          <span className="hc-sig-foot-lbl">Heat</span>
          <span className="hc-sig-foot-val" style={{ color: heat > 70 ? "#ff5d6c" : heat > 40 ? "#f5b042" : "#4ade80" }}>
            {heat}%
          </span>
        </span>
        {/* Tiny win/loss split visual */}
        {trades > 0 && (
          <span className="hc-sig-wl-split" title={`${wins} wins · ${losses} losses`}>
            <span className="hc-sig-wl-w" style={{ flexBasis: `${winPct}%` }} />
            <span className="hc-sig-wl-l" style={{ flexBasis: `${100 - winPct}%` }} />
          </span>
        )}
      </div>
    </div>
  );
}

/* ── Telegram feed row ── */
function TgFeedRow({ msg }) {
  if (!msg || typeof msg !== "object") return null;
  const rawUser = String(msg.username || "tovar");
  const user    = rawUser.toLowerCase() === "unknown" ? "tovar" : rawUser;
  const userMsg = String(msg.text || msg.user_message || "");
  const botMsg  = String(msg.response || msg.bot_response || "");
  const ts      = msg.timestamp ? newsAgo(msg.timestamp) : "";

  return (
    <div className="tg-row">
      {userMsg ? (
        <div className="tg-msg tg-msg--user">
          <div className="tg-avatar tg-avatar--user">T</div>
          <div className="tg-content">
            <div className="tg-meta">
              <span className="tg-user">@{user}</span>
              {ts ? <span className="tg-ts">{ts}</span> : null}
            </div>
            <div className="tg-bubble tg-bubble--user">
              <span className="tg-cmd">{userMsg.length > 100 ? userMsg.slice(0,100)+"…" : userMsg}</span>
            </div>
          </div>
        </div>
      ) : null}
      {botMsg ? (
        <div className="tg-msg tg-msg--bot">
          <div className="tg-avatar tg-avatar--bot">A</div>
          <div className="tg-content">
            <div className="tg-meta">
              <span className="tg-user tg-user--bot">@ObsidianCabalbot</span>
              <span className="tg-bot-tag">AEON</span>
              {!userMsg && ts ? <span className="tg-ts">{ts}</span> : null}
            </div>
            <div className="tg-bubble tg-bubble--bot">
              <span className="tg-bot-text">{botMsg.length > 240 ? botMsg.slice(0,240)+"…" : botMsg}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ══════════════════════════════════════════════════
   MAIN PAGE
══════════════════════════════════════════════════ */
function DeskSection({ index, title, description }) {
  return (
    <div className="hub-desk-section" aria-label={`${index}. ${title}`}>
      <span>{String(index).padStart(2, "0")}</span>
      <div>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <i aria-hidden />
    </div>
  );
}

export default function HubCommandPage({
  hubState, messages, btcPrice, wsConnected,
  totalPnl, winRate, totalTrades, dataAvailability,
}) {
  const [coins, setCoins]               = useState([]);
  const [positions, setPositions]       = useState([]);
  const [selectedPos, setSelectedPos]   = useState(null);
  const [news, setNews]                 = useState([]);
  const [newsLoading, setNewsLoading]   = useState(false);
  const [tgFeed, setTgFeed]             = useState([]);
  const [whaleActivity, setWhaleActivity] = useState({ activity: "—", message: "" });
  const [enginePerf, setEnginePerf] = useState({}); // { ENGINE_NAME: {trades,wins,total_pnl,win_rate,avg_pnl} }
  const [feedTimes, setFeedTimes] = useState({ market: null, news: null, positions: null });
  const newsFetched  = useRef(false);

  /* top-100 coins — refresh every 60s */
  const fetchCoins = useCallback(() => {
    fetch(`${API_URL}/api/intel/top100`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        setCoins(data.slice(0,50).map(c => ({
          symbol: String(c.symbol||c.ticker||"").toUpperCase().replace(/\/USDT|USDT$/i,""),
          price:  c.current_price ?? c.price ?? c.last_price ?? null,
          change: c.price_change_percentage_24h ?? c.change_24h ?? c.change ?? null,
        })));
        setFeedTimes(current => ({ ...current, market: Date.now() }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCoins();
    const t = setInterval(fetchCoins, 60000);
    return () => clearInterval(t);
  }, [fetchCoins]);

  /* news */
  const fetchNews = useCallback(() => {
    setNewsLoading(true);
    fetch(`${API_URL}/api/news/latest?limit=10`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        if (data.length > 0) setNews(data);
        setFeedTimes(current => ({ ...current, news: Date.now() }));
      })
      .catch(() => {})
      .finally(() => setNewsLoading(false));
  }, []);

  useEffect(() => {
    if (newsFetched.current) return;
    newsFetched.current = true;
    fetchNews();
    const t = setInterval(fetchNews, 300000);
    return () => clearInterval(t);
  }, [fetchNews]);

  /* engine performance — refresh every 60s */
  useEffect(() => {
    const fetch_ = () => {
      fetch(`${API_URL}/api/analytics/win-rates`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d && d.by_engine) setEnginePerf(d.by_engine); })
        .catch(() => {});
    };
    fetch_();
    const t = setInterval(fetch_, 60000);
    return () => clearInterval(t);
  }, []);

  /* whale activity — refresh every 2 min */
  useEffect(() => {
    const fetch_ = () => {
      fetch(`${API_URL}/api/whales/activity`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setWhaleActivity({ activity: String(d.activity || "—"), message: String(d.message || "") }); })
        .catch(() => {});
    };
    fetch_();
    const t = setInterval(fetch_, 120000);
    return () => clearInterval(t);
  }, []);

  /* telegram feed — refresh every 30s */
  const fetchTg = useCallback(() => {
    fetch(`${API_URL}/api/bot/messages?limit=20`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { if (Array.isArray(data)) setTgFeed(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchTg();
    const t = setInterval(fetchTg, 30000);
    return () => clearInterval(t);
  }, [fetchTg]);

  /* positions — refresh every 15s */
  const fetchPositions = useCallback(() => {
    fetch(`${API_URL}/api/paper/positions`)
      .then(r => r.ok ? r.json() : {})
      .then(d => {
        setPositions(d.positions || []);
        setFeedTimes(current => ({ ...current, positions: Date.now() }));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchPositions();
    const t = setInterval(fetchPositions, 15000);
    return () => clearInterval(t);
  }, [fetchPositions]);

  const closePosition = useCallback((accountId, symbol) => {
    fetch(`${API_URL}/api/paper/close`, {
      method:"POST",
      headers:{ "Content-Type":"application/json" },
      body: JSON.stringify({ account_id:accountId, symbol }),
    })
      .then(() => { setSelectedPos(null); fetchPositions(); })
      .catch(() => {});
  }, [fetchPositions]);

  /* watchlist — merge live coin data with BTC price override */
  const watchlist = useMemo(() => TRACK_SYMS.map(sym => {
    const coin  = coins.find(c => c.symbol === sym) || {};
    const price = sym === "BTC" && btcPrice ? Number(btcPrice) : (coin.price || null);
    return { symbol:sym, price, change: coin.change ?? null };
  }), [coins, btcPrice]);

  const tickerItems = useMemo(() => [
    ...watchlist,
    ...coins.filter(c => !TRACK_SYMS.includes(c.symbol)).slice(0,8).map(c => ({
      symbol:c.symbol, price:c.price, change:c.change,
    })),
  ], [watchlist, coins]);

  const engines    = useMemo(() => hubState?.engines || [], [hubState?.engines]);
  const topEngines = useMemo(() => [...engines].sort((a,b) => b.edge - a.edge).slice(0,6), [engines]);
  const activeCount = engines.filter(e => e.status === "healthy").length;

  return (
    <>
      <div className="hc-root arena-stagger">

        {/* Welcome hero */}
        <WelcomeHero
          btcPrice={btcPrice}
          hubState={hubState}
          wsConnected={wsConnected}
          coins={coins}
          news={news}
          positions={positions}
          totalPnl={totalPnl}
          winRate={winRate}
          totalTrades={totalTrades}
          whaleActivity={whaleActivity}
          feedTimes={feedTimes}
          dataAvailability={dataAvailability}
        />

        {/* Live rotating banner */}
        <LiveBanner
          news={news}
          hubState={hubState}
          btcPrice={btcPrice}
          coins={coins}
          dataAvailability={dataAvailability}
        />

        {/* Ticker strip */}
        <div className="hc-ticker-wrap">
          <span className="hc-ticker-badge"><Radio size={11} />LIVE</span>
          <div className="hc-ticker-viewport">
            <div className="hc-ticker-scroll">
              {[...tickerItems, ...tickerItems].map((w,i) => (
                <TickerItem key={i} symbol={w.symbol} price={w.price} change={w.change} />
              ))}
            </div>
          </div>
        </div>

        <div className="hub-main-stack">
          <DeskSection
            index={1}
            title="Desk now"
            description="Live exposure, prices and the strategies currently shaping the book."
          />
          <div className="hub-primary-grid">

          {/* SECTION: Live Positions */}
          <section className="hc-panel hub-section-positions">
            <header className="hc-panel-hdr">
              <Activity size={14} style={{ color:"#4ad6ff" }} />
              <span>Live Positions</span>
              <span className="arena-pulse-dot" style={{ "--pulse-color":positions.length>0?"#4ade80":"var(--text-mute)", width:6, height:6 }} />
              <span className="hc-badge" style={{ marginLeft:"auto" }}>{positions.length} open</span>
            </header>
            {positions.length === 0 ? (
              <div className="hc-trade-empty">
                <span className="hc-empty-icon"><Clock size={16} /></span>
                <span>No active positions</span>
                <small>The execution feed is clear. New positions will appear here in real time.</small>
              </div>
            ) : (
              <div className="pos-grid">
                {positions.map((pos,i) => (
                  <PositionCard key={pos.symbol||i} pos={pos} onClick={() => setSelectedPos(pos)} />
                ))}
              </div>
            )}
          </section>

          {/* SECTION: Watchlist */}
          <section className="hc-panel hub-section-watchlist">
            <header className="hc-panel-hdr">
              <Eye size={14} style={{ color:"#4ad6ff" }} />
              <span>Live Prices</span>
              <span className="hc-badge">{TRACK_SYMS.length} coins</span>
            </header>
            <div className="wl-list">
              {watchlist.map(w => <WatchRow key={w.symbol} {...w} />)}
            </div>
          </section>

          {/* SECTION: Engine Signals */}
          <section className="hc-panel hub-section-engines">
            <header className="hc-panel-hdr">
              <Target size={14} style={{ color:"#4ade80" }} />
              <span>Engine Signals</span>
              <span className="hc-badge" style={{ color:"#4ade80", borderColor:"rgba(74,222,128,0.35)" }}>
                {activeCount} active
              </span>
            </header>
            <div className="hc-sig-list">
              {topEngines.map(e => {
                // Try to match engine display name to backend key (FREE_WILL_V2 etc)
                const key = String(e.name || "").toUpperCase().replace(/\s+/g, "_").replace(/[^A-Z0-9_]/g, "");
                const perf = enginePerf[key]
                  || enginePerf[Object.keys(enginePerf).find(k => k.startsWith(key.split("_")[0])) || ""]
                  || null;
                return <SignalRow key={e.name} engine={e} perf={perf} />;
              })}
            </div>
          </section>
          </div>

          <DeskSection
            index={2}
            title="Market context"
            description="Price action is only half the story. Funding, liquidity and scheduled risk live here."
          />
          <div className="hub-market-grid">

          {/* SECTION: Market News */}
          <section className="hc-panel hub-section-news">
            <header className="hc-panel-hdr">
              <Newspaper size={14} style={{ color:"#f5b042" }} />
              <span>Market News</span>
              <button type="button" className="hc-news-refresh" onClick={fetchNews}>
                <RefreshCw size={11} className={newsLoading?"hc-spin":""} />
              </button>
            </header>
            <div className="news-list">
              {news.length === 0 ? (
                <div className="hc-trade-empty">
                  <span className="hc-empty-icon"><Newspaper size={16} /></span>
                  <span>{newsLoading ? "Loading intelligence…" : "No headlines available"}</span>
                  <small>{newsLoading ? "Connecting to market sources." : "The feed will update as new stories arrive."}</small>
                </div>
              ) : news.map((item,i) => <NewsItem key={i} item={item} />)}
            </div>
          </section>

          {/* SECTION: Derivatives Pulse — funding + OI */}
          <DerivativesStrip />

          {/* SECTION: Economic Calendar */}
          <EconomicCalendar />

          {/* SECTION: Multi-Exchange Orderbook */}
          <OrderbookView />
          </div>

          <DeskSection
            index={3}
            title="Intelligence"
            description="The broader information field, reduced to what can change a decision."
          />
          <div className="hub-intelligence-grid">

          {/* SECTION: World News Map */}
          <section className="hc-panel hub-section-worldmap">
            <header className="hc-panel-hdr">
              <Globe size={14} style={{ color:"#4ad6ff" }} />
              <span>Live News Map</span>
              {whaleActivity.activity !== "—" && (
                <span
                  className="hc-badge"
                  title={whaleActivity.message}
                  style={{
                    color: whaleActivity.activity === "HIGH" ? "#ff5d6c"
                         : whaleActivity.activity === "MED" || whaleActivity.activity === "MEDIUM" ? "#f5b042"
                         : "#4ade80",
                    borderColor: "rgba(255,255,255,0.15)",
                    background: "transparent",
                  }}>
                  Flow {whaleActivity.activity}
                </span>
              )}
              <span className="hc-badge" style={{ marginLeft:"auto" }}>{news.length} headlines</span>
            </header>
            <WorldNewsMap news={news} />
          </section>

          {/* SECTION: World Intelligence — macro + crypto feed above Telegram */}
          <WorldNewsPanel news={news} loading={newsLoading} onRefresh={fetchNews} />

          {/* SECTION: Telegram Feed */}
          <section className="hc-panel hub-section-telegram">
            <header className="hc-panel-hdr">
              <Send size={14} style={{ color:"#4ad6ff" }} />
              <span>AEON Feed</span>
              <span className="arena-pulse-dot" style={{ "--pulse-color":"#4ad6ff", width:6, height:6 }} />
              <button type="button" className="hc-news-refresh" onClick={fetchTg} style={{ marginLeft:"auto" }}>
                <RefreshCw size={11} />
              </button>
            </header>
            <div className="tg-feed-list">
              {tgFeed.length === 0 ? (
                <div className="hc-trade-empty">
                  <span className="hc-empty-icon"><Send size={16} /></span>
                  <span>No desk messages</span>
                  <small>AEON updates and operator notes will appear here.</small>
                </div>
              ) : tgFeed.map((msg, i) => <TgFeedRow key={i} msg={msg} />)}
            </div>
          </section>
          </div>

          <details className="hub-terminal-drawer">
            <summary>
              <span><Terminal size={15} /> Full market terminal</span>
              <small>Open the dense data view</small>
            </summary>
            <section className="hc-panel hub-section-terminal" style={{ padding: 0 }}>
              <BloombergTerminal
                coins={coins}
                hubState={hubState}
                btcPrice={btcPrice}
                news={news}
              />
            </section>
          </details>

        </div>
      </div>

      {selectedPos && (
        <CallingCardModal
          pos={selectedPos}
          onClose={() => setSelectedPos(null)}
          onClosePosition={closePosition}
        />
      )}
    </>
  );
}
